import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addCatalogDownloadItems,
  clearSelectedItemIds,
  ConversationInfo,
  CustomerGroupMember,
  CustomersReportResponse,
  GroupCustomerWithMetrics,
  dispatch,
  getCatalogDownloadStatus,
  getCustomerProfileSyncStatusForCustomer,
  GetGroupMembersResponse,
  getIsSelectedAll,
  getWorkspaceId,
  updateExtraDetails,
  updateIsSelectedAll,
  updateSelectedItemIds,
  useAppSelector,
  useGetCustomerReportMutation,
  useLazyGetGroupMembersQuery,
} from '@zonofi/zono-money-store';
import { FIELD_OPS_ROUTES } from '../../constants';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDebounceSearch, useLoadAllApiDataV2 } from '../../hooks';
import { useCustomerProfileFetcher } from './use-customer-profile-fetcher';
import { useNotificationToast } from '../../notification';
import { customApiQueue } from '../../queue/request';

const getLatestConversation = (conversations?: ConversationInfo[]) => {
  if (!conversations || conversations?.length === 0) return undefined;

  const latest = [...conversations].sort(
    (a, b) =>
      new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
  )[0];

  return {
    conversationId: latest?.id,
    tags: latest?.metadata?.tags ?? [],
    summary: latest?.summary ?? latest?.metadata?.summary,
    latestMessage: latest?.lastMessage,
  };
};

export const useCustomerRouteDetails = () => {
  const navigate = useNavigate();
  const { routeId } = useParams();
  const groupId = routeId;
  const [searchParams] = useSearchParams();
  const beatPlanId = searchParams.get('beatPlanId');

  const { fetchProfileLowPriority, fetchProfileHighPriority } =
    useCustomerProfileFetcher();

  const { onShowToast, onCloseToast } = useNotificationToast();

  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const isSelectedAll = useAppSelector(getIsSelectedAll);
  const customerProfileSyncStatus = useAppSelector(
    getCustomerProfileSyncStatusForCustomer
  );
  const catalogDownloadStatusMap = useAppSelector(getCatalogDownloadStatus);
  const { debouncedSearchValue } = useDebounceSearch();

  const [getRouteCustomerListAPI, getRouteCustomerListResponse] =
    useLazyGetGroupMembersQuery();
  const [getCustomerReportAPI] = useGetCustomerReportMutation();

  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  // ─── Accumulated metrics map across all pages ─────────────────────
  // Keyed by inviteid (= customerId). Grows as each page's report resolves.
  const metricsMapRef = useRef<Record<string, CustomersReportResponse>>({});
  const [metricsVersion, setMetricsVersion] = useState(0);

  const fetchFieldOpsCustomerList = useCallback(
    (params: { pageNo: number; maxPageSize: number }) => {
      // Reset accumulated metrics on fresh load (page 1)
      if (params.pageNo === 1) {
        metricsMapRef.current = {};
        setMetricsVersion(0);
      }

      getRouteCustomerListAPI({
        sellerWorkspaceId,
        pageNo: params.pageNo,
        pageSize: params.maxPageSize,
        groupId: groupId || '',
        isIncludeLifeCycleGroups: true,
        isConversation: true,
      })
        .unwrap()
        .then((response) => {
          const customerIds = response.customerGroupMembers.map(
            (x) => x.customerId
          );
          if (customerIds.length === 0) return;

          getCustomerReportAPI({
            params: { sellerWorkspaceId },
            body: {
              customerIds,
              sellerworkspaceid: sellerWorkspaceId,
            },
          })
            .unwrap()
            .then((metrics) => {
              // Accumulate this page's metrics — never overwrite previous pages
              metrics.forEach((item: any) => {
                metricsMapRef.current[item.inviteid] = item;
              });
              setMetricsVersion((v) => v + 1);
            })
            .catch((err) => {
              console.error('Error fetching customer report:', err);
              // Still bump version so memo re-runs and falls back gracefully
              setMetricsVersion((v) => v + 1);
            });
        });
    },
    [sellerWorkspaceId, groupId, beatPlanId]
  );

  const {
    data: fieldOpsCustomer, // searched + filtered — for UI rendering only
    raw: rawFieldOpsCustomer, // full unfiltered list — for all background ops
    completed,
    isPageLoading,
    refetch,
  } = useLoadAllApiDataV2<CustomerGroupMember, GetGroupMembersResponse>({
    fetchDataApiCall: fetchFieldOpsCustomerList,
    apiResponse: getRouteCustomerListResponse,
    transformer: {
      getData: (res) => res?.customerGroupMembers ?? [],
      getPagination: (res) => ({
        hasNextPage: (res?.endRecord ?? 0) < (res?.totalCount ?? 0),
      }),
    },
    uniqueKey: 'id',
    maxPageSize: 100,
    dependencies: [groupId],
    searchText: debouncedSearchValue,
    searchPaths: ['name', 'companyName'],
  });

  // ─── Merge accumulated metrics against the full raw list ──────────
  // Using rawFieldOpsCustomer ensures search/filter never cuts off
  // customers from receiving their enriched metrics data.
  const fieldOpsCustomerWithMetrics = useMemo(() => {
    if (!rawFieldOpsCustomer || rawFieldOpsCustomer.length === 0) return [];

    return rawFieldOpsCustomer.map((customer) => {
      const metric = metricsMapRef.current[customer.customerId];
      const latestConversation = getLatestConversation(
        customer?.conversations?.data
      );
      const { conversations, ...restCustomer } = customer;

      if (!metric) {
        return { ...restCustomer, conversation: latestConversation };
      }

      return {
        ...restCustomer,
        conversation: latestConversation,
        routes: metric.routes
          ? metric.routes.split(',').map((r: string) => r.trim())
          : [],
        segments: metric.segments
          ? metric.segments.split(',').map((s: string) => s.trim())
          : [],
        outstandingamount: metric.outstandingAmount,
        lastinvoicedate: metric.latestInvoiceDate,
      };
    });
  }, [rawFieldOpsCustomer, metricsVersion]);

  // ─── Searched + filtered view enriched with metrics — for UI ──────
  // fieldOpsCustomer (filtered) is matched against the metrics map so
  // the rendered list is also enriched, just scoped to search results.
  const fieldOpsCustomerFiltered: GroupCustomerWithMetrics[] = useMemo(() => {
    if (!fieldOpsCustomer || fieldOpsCustomer.length === 0) return [];

    return fieldOpsCustomer.map((customer) => {
      const metric = metricsMapRef.current[customer.customerId];
      const latestConversation = getLatestConversation(
        customer?.conversations?.data
      );
      const { conversations, ...restCustomer } = customer;

      if (!metric) {
        return { ...restCustomer, conversation: latestConversation };
      }

      return {
        ...restCustomer,
        conversation: latestConversation,
        routes: metric.routes
          ? metric.routes.split(',').map((r: string) => r.trim())
          : [],
        segments: metric.segments
          ? metric.segments.split(',').map((s: string) => s.trim())
          : [],
        outstandingamount: metric.outstandingAmount,
        lastinvoicedate: metric.latestInvoiceDate,
      };
    });
  }, [fieldOpsCustomer, metricsVersion]);

  // ─── All background ops use raw list ─────────────────────────────

  const allCustomerProfilesSynced = useMemo(() => {
    if (!fieldOpsCustomerWithMetrics.length) return false;
    return fieldOpsCustomerWithMetrics.every((customer) =>
      customerProfileSyncStatus(customer.customerId)
    );
  }, [fieldOpsCustomerWithMetrics, customerProfileSyncStatus]);

  const customerCountMismatching = useMemo(() => {
    return (
      !!getRouteCustomerListResponse?.data &&
      fieldOpsCustomerWithMetrics.length <
        (getRouteCustomerListResponse.data.totalCount ?? 0)
    );
  }, [getRouteCustomerListResponse, fieldOpsCustomerWithMetrics]);

  useEffect(() => {
    if (
      completed &&
      fieldOpsCustomerWithMetrics.length > 0 &&
      !allCustomerProfilesSynced
    ) {
      dispatch(
        addCatalogDownloadItems(
          fieldOpsCustomerWithMetrics.map((customer) => customer?.id) || []
        )
      );
    }
  }, [completed, fieldOpsCustomerWithMetrics]);

  useEffect(() => {
    if (
      !isPageLoading &&
      completed &&
      Object.keys(catalogDownloadStatusMap).length ===
        fieldOpsCustomerWithMetrics.length &&
      (!allCustomerProfilesSynced || customerCountMismatching)
    ) {
      let customersForProfileDownload = Object.entries(catalogDownloadStatusMap)
        .filter(([_, value]) => value.profile === false)
        .map(([key]) => ({ customerId: key }));

      if (customersForProfileDownload.length === 0) {
        customersForProfileDownload = fieldOpsCustomerWithMetrics;
      }

      const profileCalls = customersForProfileDownload.map((customer) =>
        fetchProfileLowPriority(customer.customerId)
          .then(() => {})
          .catch((err) => {
            console.error(
              'Error fetching customer profile (bulk sync): ' +
                customer.customerId,
              err
            );
          })
      );

      if (profileCalls.length > 0) {
        Promise.all(profileCalls)
          .then(() => {
            console.log('Fetched Customer Profile Data for route:', groupId);
          })
          .catch((err) => {
            console.error('Error fetching one or more profiles:', err);
          });
      }
    }
  }, [
    fieldOpsCustomerWithMetrics,
    allCustomerProfilesSynced,
    isPageLoading,
    completed,
    customerCountMismatching,
    catalogDownloadStatusMap,
  ]);

  // ─── Interactions ─────────────────────────────────────────────────

  const onSelectItem = (id: string) => {
    dispatch(updateSelectedItemIds(id));
  };

  const onClickCustomer = (
    customerId: string,
    customerName: string,
    companyName: string,
    mobile?: string
  ) => {
    customApiQueue.cancelTaskById(customerId);
    fetchProfileHighPriority(customerId)
      .then(() => {})
      .catch((err) => {
        console.error(
          'Error fetching customer profile (high priority): ' + customerId,
          err
        );
      });
    dispatch(
      updateExtraDetails({
        customerId,
        customerName: customerName ?? '',
        companyName,
        ...(mobile && { mobile }),
      })
    );
    navigate(
      `${FIELD_OPS_ROUTES.CUSTOMER_PROFILE?.replace(':customerId', customerId)}`
    );
  };

  // Select all uses raw so search doesn't shrink the selection
  const handleToggleSelectAll = useCallback(() => {
    const next = !isSelectedAll;
    dispatch(updateIsSelectedAll(next));
    if (next) {
      const allCustomerIds =
        fieldOpsCustomerWithMetrics.map((customer) => customer?.customerId) ??
        [];
      dispatch(updateSelectedItemIds(allCustomerIds));
    } else {
      dispatch(clearSelectedItemIds());
    }
  }, [fieldOpsCustomerWithMetrics, completed, isSelectedAll]);

  const handleClearSelection = () => {
    dispatch(clearSelectedItemIds());
    setShowCheckboxes(false);
  };

  const refetchRouteCustomers = useCallback(() => {
    metricsMapRef.current = {};
    setMetricsVersion(0);
    refetch();
  }, [refetch]);

  return {
    fieldOpsCustomer: fieldOpsCustomerFiltered, // enriched + search-filtered for UI
    onSelectItem,
    onClickCustomer,
    isLoading:
      isPageLoading ||
      getRouteCustomerListResponse?.isUninitialized ||
      getRouteCustomerListResponse?.isFetching,
    showCheckboxes,
    setShowCheckboxes,
    getRouteCustomerListResponse,
    showSearch,
    setShowSearch,
    handleToggleSelectAll,
    handleClearSelection,
    refetchRouteCustomers,
  };
};
